import { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useProductSearch } from '../hooks/useProductSearch';
import { ProductSearchResult, ProductVariantResult } from '../services/customerService';
import { formatCurrency } from '../../../lib/utils';
import { useAuthStore } from '../../../stores';

export interface SelectedVariant {
  variantId: string;
  productTitle: string;
  variantTitle: string;
  sku: string | null;
  price: string;
}

interface ProductSearchInputProps {
  onSelect: (variant: SelectedVariant) => void;
  selectedVariant: SelectedVariant | null;
  onClear: () => void;
}

export default function ProductSearchInput({ onSelect, selectedVariant, onClear }: ProductSearchInputProps) {
  const currency = useAuthStore((s) => s.currency);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const { products, isLoading, hasQuery } = useProductSearch(query);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (selectedVariant) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink">
        <div className="flex-1 truncate">
          <span className="font-medium">{selectedVariant.productTitle}</span>
          {selectedVariant.variantTitle !== 'Default Title' && (
            <span className="text-ink-3"> — {selectedVariant.variantTitle}</span>
          )}
          {selectedVariant.sku && (
            <span className="ml-1 text-ink-2">({selectedVariant.sku})</span>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-ink-2 hover:text-danger text-xs font-medium"
        >
          Change
        </button>
      </div>
    );
  }

  const handleSelectVariant = (product: ProductSearchResult, variant: ProductVariantResult) => {
    onSelect({
      variantId: variant.id,
      productTitle: product.title,
      variantTitle: variant.title,
      sku: variant.sku,
      price: variant.price,
    });
    setQuery('');
    setIsOpen(false);
    setExpandedProductId(null);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
        <input
          type="text"
          placeholder="Search by title, SKU, or part number..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="w-full rounded-xl border border-line bg-surface text-ink placeholder:text-ink-3 pl-9 pr-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-brand" />
          </div>
        )}
      </div>

      {isOpen && hasQuery && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-card border border-line bg-surface shadow-m">
          {products.length === 0 && !isLoading ? (
            <div className="p-3 text-center text-sm text-ink-3">No products found</div>
          ) : (
            products.map((product) => (
              <div key={product.id} className="border-b border-line last:border-b-0">
                <button
                  type="button"
                  onClick={() => {
                    if (product.variants.length === 1 && product.variants[0]) {
                      handleSelectVariant(product, product.variants[0]);
                    } else {
                      setExpandedProductId(
                        expandedProductId === product.id ? null : product.id
                      );
                    }
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-black/[0.04]"
                >
                  {product.image && (
                    <img src={product.image} alt="" className="h-8 w-8 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-ink">
                      {product.title}
                    </div>
                    <div className="flex gap-2 text-xs text-ink-3">
                      {product.partNumber && <span>Part#: {product.partNumber}</span>}
                      {product.vendor && <span>{product.vendor}</span>}
                    </div>
                  </div>
                  {product.variants.length > 1 && (
                    <span className="text-xs text-ink-2">
                      {product.variants.length} variants
                    </span>
                  )}
                </button>

                {expandedProductId === product.id && product.variants.length > 1 && (
                  <div className="bg-surface-2">
                    {product.variants.map((variant) => (
                      <button
                        type="button"
                        key={variant.id}
                        onClick={() => handleSelectVariant(product, variant)}
                        className="flex w-full items-center justify-between px-3 py-1.5 pl-6 text-left hover:bg-black/[0.04]"
                      >
                        <div className="text-xs">
                          <span className="text-ink-2">{variant.title}</span>
                          {variant.sku && (
                            <span className="ml-1 text-ink-3">SKU: {variant.sku}</span>
                          )}
                          {variant.partNumber && (
                            <span className="ml-1 text-ink-3">Part#: {variant.partNumber}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-ink-2">{formatCurrency(Number(variant.price), currency)}</span>
                          <span className={variant.inventoryQuantity > 0 ? 'text-success' : 'text-danger'}>
                            {variant.inventoryQuantity > 0
                              ? `${variant.inventoryQuantity} in stock`
                              : 'Out of stock'}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
